import { LoanCalculations, LoanOption } from '@oneethos/shared'
import { Section } from './section'
import { useMemo } from 'react'

type LoanInformationProps = {
  isResidential: boolean
  isStorageOnly?: boolean
  loanOptions: LoanOption[]
}

const getSuffix = (isStorageOnly: boolean, isResidential: boolean) => {
  if (isStorageOnly) return 'for storage only financing'
  if (isResidential) return 'for residential solar financing'
  return 'for commercial / non-profit solar financing'
}

export const LoanInformation = ({
  isResidential,
  isStorageOnly = false,
  loanOptions
}: LoanInformationProps) => {

  const loanTermInYears = useMemo(() => {
    const terms = loanOptions.map((loanOption) => {
      return LoanCalculations.calculateLoanTermInYears(loanOption.product.term)
    })
    const uniqueTerms = Array.from(new Set(terms))
    return uniqueTerms.sort((a, b) => Number(b) - Number(a))
  }, [loanOptions])

  const loanTermText = useMemo(() => {
    if (loanTermInYears.length === 0) return ''

    const suffix = getSuffix(isStorageOnly, isResidential)

    if (loanTermInYears.length === 1) {
      return `${loanTermInYears[0]}-year loan term option ${suffix}`
    }

    const allButLast = loanTermInYears.slice(0, -1)
    const last = loanTermInYears.at(-1)
    return `${allButLast.join(', ')}, or ${last}-year loan term options ${suffix}`
  }, [loanTermInYears, isStorageOnly, isResidential])

  return (
    <Section>
      <div className="section-title">
        <h2 className="margin-bottom-0">Loan Information</h2>
      </div>
      <h5 className="mt-3 mb-3"><b>What You May Share</b></h5>
      <ul className="list text d-flex flex-column gap-2">
        <li>
          <b>Estimated Monthly Payment</b> — You may share the estimated monthly payment generated by the
          Climate First Bank Loan Payment Calculator available on the Installer Portal. Please note that
          this is an estimate only and is subject to change based on the customer's final credit approval,
          loan amount, and terms determined by Climate First Bank.
        </li>
        <li>
          <b>Available Loan Terms</b> — You may inform customers that Climate First Bank offers {loanTermText}.
        </li>
      </ul>
      {
        isResidential && (
          <>
            <h5 className="mt-3 mb-3"><b>What Must Be Discussed by a Climate First Bank Banker</b></h5>
            <ul className="list text d-flex flex-column gap-2">
              <li>
                All other loan details — including but not limited to interest rates, fees, credit requirements,
                approval criteria, underwriting decisions, and specific loan terms <b>will be explained directly
                  by a Climate First Bank banker during the Welcome Call.</b>
              </li>
            </ul>
          </>
        )
      }
    </Section>
  )
}

export default LoanInformation